import { Button, Checkbox, DatePicker, Input, Popover, Radio } from 'antd'
import { getId, getType } from 'apis/model/base'
import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import { FormActionGroups } from 'components/form/FormActionBar'
import { createValue } from 'components/form/utils'
import { Formik } from 'formik'
import { getDateString, parseDayjs } from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, { useMemo, useState } from 'react'
import { IoCalendarOutline } from 'react-icons/io5'
import { compose, mapProps, nest, pure } from 'recompose'
import { ErrorSummaries } from 'views/AppLocals/factory/createEditableForm'
import { deepTranslate, NullAsDefault, shallowDiff } from 'views/Shared'
import { convertFromString, convertToString } from 'views/SlateEditor/functions'
import { ModalEditorToolbar } from 'views/SlateEditor/SlateEditor'
import { course_status_options } from '..'
import DynamicFieldsDeclaration from '../../../../components/DynamicFields/DynamicFieldDeclaration'
import FormActionBar from '../../../../components/form/FormActionBar'
import SlateEditor from '../../../SlateEditor'
import { MedalSingleSelect } from '../../../Workspace/Medal/MedalSelect'
import { appClassNames } from '../../custom/appClassNames'
import {
  createControlledFormField,
  createNumberControlledInput,
} from '../../factory/createFormField'
import OrganizationContainerContext from '../../Organization/OrganizationContainerContext'
import { PiChalkboardSimpleBold } from 'react-icons/pi'
import { GoDotFill } from 'react-icons/go'
import { renderIfElse } from '../../../Shared'
import { LuCornerLeftDown, LuCornerRightDown } from 'react-icons/lu'
import { TbSum, TbTimelineEventText } from 'react-icons/tb'
import TextArea from 'antd/lib/input/TextArea'
import { CustomToggleField } from '../../../../components/form/fields/ToggleField'
import Pure from '../../../../components/Pure'
import {
  HeaderWithIconLine,
  ListHeaderLineAndDes,
} from '../../Templates/items/ListHeader'
import Translate from '../../../../modules/local/Translate'
import { ClassroomSvg } from '../../../../components/Svgs/ClassroomSvg'
import { BsShieldFillExclamation } from 'react-icons/bs'
import LookupField from '../../../../components/LookupField'
import { LocalContext } from '../../../../modules/local/LocalContext'
import { SelectWidget } from '../../custom/ReactSelectComponents'
import filterOption from '../../../../helpers/filterOption'
import getTitle from '../../../../helpers/getTitle'
import dayjs from 'dayjs'
import { ActionModalWrapper } from '../../../../components/form/ActionModalWrapper'
import { ROOT_URL } from '../../../../envs/_current/config'
import TooltipApp from '../../../../components/TooltipApp'
import {
  lengthBetween,
  noDoubleSpaces,
  noLeadingTrailingSpaces, noNumber,
  noSpecialChars,
  required,
  runRules,
} from '../../../../components/form/ValidateForm'

const AdditionalFields = ({
  name,
  value,
  defaultValue = {},
  allOptions = [
    { label: 'payment status', value: 'payment_status' },
    { label: 'religious order', value: 'religious_order' },
  ],
  handleChange = NullAsDefault,
}) => {
  const t = useTranslate()
  const _defaultValue = Object.keys(defaultValue || {}).filter(
    (key) => _.get(defaultValue, key) === 1
  )
  const allKeys = _.map(allOptions, 'value')
  return (
    <div className="flex flex-col gap-3">
      <Checkbox.Group
        name={name}
        className="flex flex-col gap-1"
        defaultValue={_defaultValue}
        onChange={(checked_values) => {
          const result = allKeys.reduce((acc, key) => {
            acc[key] = checked_values.includes(key) ? 1 : 0
            return acc
          }, {})

          handleChange(result)
        }}>
        {allOptions.map(({ label, value }, index) => (
          <Checkbox
            key={index}
            value={value}
            label={t(label)}
            className="py-1 text-sm text-color-200">
            {t(label)}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  )
}

export const formSchema = {
  status: {
    title: 'status',
    children: [
      {
        name: 'status',
        component: ({ name, value, form, onChange }) => {
          const options = Array.from(form?.values?.status_options ?? [])
          return (
            <Radio.Group
              name={name}
              value={value}
              options={options}
              onChange={onChange}
              optionType="button"
              buttonStyle="solid"
              className="custom-radio-button"
            />
          )
        },
      },
    ],
  },
  academic_year: {
    inline: true,
    className: appClassNames.inline_2,
    children: [
      {
        name: 'academic_year',
        label: 'academic year',
        component: createNumberControlledInput({
          placeholder: 'academic year',
          type: 'number',
          formatter: (value) =>
            `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
          parser: (value) => {
            const parsed = value.replace(/\$\s?|(,*)/g, '')
            return parsed ? Number(parsed) : 0 // ✅ convert sang number
          },
        }),
      },
    ],
  },
  academic_field: {
    title: 'academic field',
    children: [
      {
        name: 'academic_field',
        component: ({ name, value, onChange, form }) => {
          const values = _.get(form, 'values', {})
          const owner = _.get(values, 'owner', {})
          return (
            <Translate>
              {(t) => (
                <LookupField
                  createIfNotExists={true}
                  defaultValue={values?.academic_field}
                  apiPath={`/lookup/${getType(owner)}/${getId(
                    owner
                  )}/field/academic_field/gets`}
                  name={name}
                  placeholder={t('choose or create title')}
                  handleChange={(e) => {
                    onChange(createValue('academic_field', e.target.value))
                  }}
                />
              )}
            </Translate>
          )
        },
      },
    ],
  },
  title: {
    title: 'title of course',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title of course',
          showCount: true,
          maxLength: 180,
          className: 'rounded-md',
        }),
      },
    ],
  },
  description: {
    title: 'short description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 5,
          showCount: true,
          maxLength: 500,
          placeholder: 'short description',
          Component: TextArea,
          className: 'scrollBarThin',
        }),
      },
    ],
  },
  idname: {
    title: 'url',
    children: {
      name: 'idname',
      component: compose(
        mapProps(({ onChange, value }) => ({
          onChange,
          value,
          name: 'idname',
          addonBefore: 'https://.../course/',
          placeholder: 'please input name!',
          type: 'text',
          trigger: ['focus'],
          title: ROOT_URL + '/course/' + value,
          placement: 'topLeft',
        })),
        pure
      )(nest(TooltipApp, Input)),
    },
  },
  privacy: {
    title: 'privacy',
    children: {
      name: 'privacy',
      component: compose(
        withTranslate,
        mapProps(({ name, value, onChange, translate }) => ({
          name,
          value: Number(value) === 2,
          onChange: (value) => {
            onChange(createValue(name, Boolean(value) ? 2 : 1))
          },
          title: translate('privacy'),
          description: translate(
            'This parameter allows whether non-member users are restricted from access or not.'
          ),
        }))
      )(CustomToggleField),
    },
  },
  channel_post_permission: {
    title: 'posting permission',
    children: {
      name: 'channel_post_permission',
      component: compose(
        withTranslate,
        mapProps(({ name, value, onChange, translate }) => ({
          name,
          value: Number(value) === 3,
          onChange: (value) => {
            onChange(createValue(name, Boolean(value) ? 3 : 2))
          },
          title: translate('posting permission'),
          description: translate(
            'Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post'
          ),
        }))
      )(CustomToggleField),
    },
  },
  about: {
    title: 'about course',
    children: [
      {
        name: 'about',
        component: createControlledFormField({
          Component: SlateEditor,
          ToolbarComponent: ModalEditorToolbar,
          className: 'background border rounded-lg',
          withProps: ({ value }) => ({
            defaultValue: value,
          }),
          withValues: ({ resetKey }) => ({
            resetKey,
          }),
        }),
      },
    ],
  },
  last_enrollment_time: {
    title: 'last enrollment time',
    children: [
      {
        name: 'last_enrollment_time',
        component: compose(
          withTranslate,
          mapProps(({ name, value, onChange, translate }) => ({
            name,
            value,
            showTime: true,
            className: 'w-full',
            format: 'HH:mm - DD/MM/YYYY',
            suffixIcon: <IoCalendarOutline className="text-color-500" />,
            placeholder: translate('ex: 07:00 - 20/10/2010'),
            // Chỉ cho chọn trong khoảng 100 năm
            disabledDate: (current) => {
              const minDate = dayjs().subtract(100, 'year').startOf('day')
              const maxDate = dayjs().add(100, 'year').endOf('day')
              return current && (current < minDate || current > maxDate)
            },
            onChange: (date, dateString) => {
              onChange(createValue(name, date ? dayjs(new Date(date)) : null))
            },
          }))
        )(DatePicker),
      },
    ],
  },
  started_finished_date: {
    inline: true,
    className: appClassNames.inline_3,
    children: [
      {
        name: 'time_open',
        label: 'start date',
        component: compose(
          withTranslate,
          mapProps(({ name, value, onChange, translate, form }) => ({
            name,
            value,
            // showTime: true,
            className: 'w-full',
            format: 'DD/MM/YYYY',
            suffixIcon: <IoCalendarOutline className="text-color-500" />,
            placeholder: translate('ex: 20/10/2010'),
            // Chỉ cho chọn trong khoảng 100 năm
            disabledDate: (current) => {
              const minDate = dayjs().subtract(100, 'year').startOf('day')
              const maxDate = dayjs().add(100, 'year').endOf('day')
              return current && (current < minDate || current > maxDate)
            },
            onChange: (date) => {
              const nextDate = date ? dayjs(date) : null
              form.setValues({
                ...form.values,
                time_open: nextDate,
                academic_year: nextDate?.year()
              }, true)
            }
          }))
        )(DatePicker),
      },
      {
        name: 'time_close',
        label: 'end date',
        component: compose(
          withTranslate,
          mapProps(({ name, value, onChange, translate, form }) => {
            const timeOpen = form?.values?.time_open
            return {
              name,
              value,
              // showTime: true,
              disabled: !timeOpen,
              className: 'w-full',
              format: 'DD/MM/YYYY',
              suffixIcon: <IoCalendarOutline className="text-color-500" />,
              placeholder: translate('ex: 20/10/2010'),
              // Chỉ cho chọn trong khoảng 100 năm
              disabledDate: (current) => {
                const minDate = dayjs().subtract(100, 'year').startOf('day')
                const maxDate = dayjs().add(100, 'year').endOf('day')
                return current && (current < minDate || current > maxDate)
              },
              onChange: (date, dateString) => {
                onChange(createValue(name, date ? dayjs(new Date(date)) : null))
              },
            }
          })
        )(DatePicker),
      },
      {
        name: 'academic_year',
        label: 'academic year',
        component: createNumberControlledInput({
          placeholder: 'academic year',
          type: 'number',
          min: 2000,
          max: 9999,
          formatter: (value) =>
              `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
          parser: (value) => {
            const parsed = value.replace(/\$\s?|(,*)/g, '')
            return parsed ? Number(parsed) : undefined
          },
          withForm: (form) => ({
            disabled: !form?.values?.time_open,
          }),
        }),
      }
    ],
  },

  medal_id: {
    title: 'certification awarded',
    children: [
      {
        name: 'medal_id',
        component: ({ name, form, value, onChange }) => {
          const defaultValue = form?.values?.medal_id
          return (
            <Translate>
              {(t) => (
                <OrganizationContainerContext.Consumer>
                  {({ organization }) => (
                    <MedalSingleSelect
                      {...{
                        name,
                        value,
                        onChange,
                        defaultValue,
                      }}
                      className="custom-select-medal"
                      placeholder={t('click to select')}
                      type="medal"
                      owner_id={getId(organization)}
                      owner_type={getType(organization)}
                    />
                  )}
                </OrganizationContainerContext.Consumer>
              )}
            </Translate>
          )
        },
      },
    ],
  },

  metadata: {
    title: 'manage student information by course',
    children: [
      {
        name: 'metadata',
        component: ({ name, value, onChange, errors }) => {
          return (
            <DynamicFieldsDeclaration
              name={name}
              value={value}
              className="w-full"
              onChange={(value) => {
                onChange(createValue(name, value))
              }}
              errors={errors?.metadata || {}}
            />
          )
        },
      },
    ],
  },

  additional_fields: {
    children: [
      {
        name: 'additional_fields',
        component: createControlledFormField({
          className: 'w-full',
          Component: AdditionalFields,
          withProps: ({ name, value, onChange }) => ({
            defaultValue: value,
            handleChange: (_value) => {
              onChange(createValue(name, _value))
            },
          }),
          withValues: ({ resetKey }) => ({
            resetKey,
          }),
        }),
      },
    ],
  },

  categories: {
    title: 'categories',
    children: [
      {
        name: 'categories',
        component: ({ name, value, onChange }) => (
          <Pure input={[value]}>
            <Translate>
              {(t) => (
                <LocalContext.Consumer>
                  {({ appConfig }) => {
                    const { categories } = appConfig || {}
                    const options = Array.from(categories || []).map(
                      (category) => ({
                        label: t(getTitle(category)),
                        value: category?.id,
                        ...category,
                      })
                    )
                    return (
                      <SelectWidget
                        value={value}
                        mode="multiple"
                        className="w-full"
                        placeholder={t('click to select')}
                        showSearch={true}
                        allowClear={true}
                        options={options}
                        filterOption={filterOption}
                        onChange={(value, option) => {
                          onChange(createValue(name, value))
                        }}
                      />
                    )
                  }}
                </LocalContext.Consumer>
              )}
            </Translate>
          </Pure>
        ),
      },
    ],
  },
}

const validateTitle = (name, t) => {
  const rules = [
    required(t),
    lengthBetween(2, 180, t),
    noSpecialChars(t),
    noLeadingTrailingSpaces(t),
    noDoubleSpaces(t),
  ].filter(Boolean)
  const message = runRules(name, rules)
  if (message) return { title: message }
}

const validateDescription = (description, t) => {
  const rules = [lengthBetween(1, 500, t)].filter(Boolean)
  const message = runRules(description, rules)
  if (message) return { description: message }
}

const validateAcademicField = (name, t) => {
  const rules = [
    lengthBetween(2, 255, t),
    noNumber(t),
    noSpecialChars(t),
    noLeadingTrailingSpaces(t),
    noDoubleSpaces(t),
  ].filter(Boolean)
  const message = runRules(name, rules)
  if (message) return { academic_field: message }
}

const validateTimeOpen = (timeOpen, typeForm, t) => {
  const rules = [required(t)].filter(Boolean)
  const message = runRules(timeOpen, rules)
  if (message) return { time_open: message }
  if (typeForm === 'Create') {
    const selected = dayjs(timeOpen).startOf('day')
    const today = dayjs().startOf('day')
    if (selected.isBefore(today)) {
      return { time_open: t('start date must be today or later') }
    }
  }
}

const validateTimeClose = (timeOpen, timeClose, t) => {
  const rules = [required(t)].filter(Boolean)
  const message = runRules(timeClose, rules)
  if (message) return { time_close: message }

  const start = dayjs(timeOpen).startOf('day')
  const end = dayjs(timeClose).startOf('day')
  if (end.isBefore(start)) {
    return { time_close: t('end date must be same as or after start date') }
  }
}

const validateAcademicYear = (value, t) => {
  const rules = [required(t)].filter(Boolean)
  const message = runRules(value, rules)
  if (message) return { academic_year: message }
}

const validateLastEnrollmentTime = (timeOpen, last_enrollment_time, t) => {
  const error = {}
  if (!timeOpen || !last_enrollment_time) return error
  const start = dayjs(timeOpen).startOf('day')
  const enrollmentDeadline = dayjs(last_enrollment_time).startOf('day')
  if (enrollmentDeadline.isAfter(start)) {
    return {
      last_enrollment_time: t('last enrollment time must be before or equal to start date'),
    }
  }

  return error
}

const validate = (t, typeForm) => (values) => {
  const {
    title,
    description,
    academic_field,
    time_open,
    time_close,
    academic_year,
    about,
    last_enrollment_time,
    metadata,
  } = values ?? {}
  const errorTitle = validateTitle(title, t)
  const errorDescription = validateDescription(description, t)
  const errorAcademicField = validateAcademicField(academic_field, t)
  const errorTimeOpen = validateTimeOpen(time_open, typeForm, t)
  const errorTimeClose = validateTimeClose(time_open, time_close, t)
  const errorAcademicYear = validateAcademicYear(academic_year, t)
  const errorLastEnrollmentTime = validateLastEnrollmentTime(
    time_open,
    last_enrollment_time,
    t
  )

  // ── validate metadata ──────────────────────────────────────────
  const metaItems = Array.isArray(metadata) ? metadata : []

  if (metaItems.length === 0) {
    // bỏ dòng này nếu metadata không bắt buộc phải có ít nhất 1 field
    // errors.metadata = t('required field')
  }

  const metaErrors = {}

  metaItems.forEach(function (item, index) {
    // title bắt buộc
    if (!item.title || !item.title.trim()) {
      metaErrors['[' + index + '].title'] = t('required field')
    } else if (item.title.trim().length > 120) {
      metaErrors['[' + index + '].title'] = t('title_max_120')
    }

    // description max 500
    if (item.description && item.description.trim().length > 500) {
      metaErrors['[' + index + '].description'] = t('description_max_500')
    }

    // options bắt buộc nếu là radioField / checkboxField
    if (['radioField', 'checkboxField'].includes(item.ctrl_type)) {
      var opts = Array.isArray(item.options) ? item.options : []
      if (opts.length === 0) {
        metaErrors['[' + index + '].options'] = t('options_min_1')
      } else {
        opts.forEach(function (opt, ii) {
          if (!opt || !String(opt).trim()) {
            metaErrors['[' + index + '].options[' + ii + ']'] =
              t('required field')
          } else if (String(opt).trim().length > 120) {
            metaErrors['[' + index + '].options[' + ii + ']'] =
              t('option_max_120')
          }
        })
      }
    }
  })

  return {
    ...errorTitle,
    ...errorDescription,
    ...errorAcademicField,
    ...errorTimeOpen,
    ...errorTimeClose,
    ...errorAcademicYear,
    ...errorLastEnrollmentTime,
    // ...(Object.keys(metaErrors).length > 0
    //   ? { metadata: t('invalid data') }
    //   : {}),
  }
}

export const CreateCourseModal = ({
  owner_id,
  owner_type,
  onCancel = NullAsDefault,
  onSubmit = NullAsDefault,
}) => {
  const t = useTranslate()

  const initialValues = useMemo(() => {
    return {
      owner_id,
      owner_type,
      title: undefined,
      description: undefined,
    }
  }, [owner_id, owner_type])

  return (
    <Formik
      validate={validate(t, "Create")}
      validateOnMount={true}
      validateOnChange={true}
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values) => {
        const {
          about,
          resetKey,
          owner_id,
          owner_type,
          time_open,
          time_close,
          last_enrollment_time,
          ...rest
        } = values ?? {}
        onSubmit(
          _.omitBy(
            {
              ...rest,
              time_open: getDateString(time_open),
              time_close: getDateString(time_close),
              about: about ? convertToString(about) : undefined,
              last_enrollment_time: getDateString(last_enrollment_time),
            },
            _.isUndefined
          )
        )
      }}>
      <React.Fragment>
        <ActionModalWrapper
          action={
            <FormActionGroups
              submitProps={{
                label: 'create',
              }}
            />
          }>
          <div className="flex flex-col gap-3">
            <Pure>
              <FieldsFactory
                formSchema={[
                  formSchema.title,
                  formSchema.description,
                  formSchema.academic_field,
                  formSchema.started_finished_date,
                  formSchema.about,
                ]}
              />
            </Pure>

            <div className="flex flex-col gap-3">
              <HeaderWithIconLine
                className="pt-3"
                renderTitle={() => (
                  <div className="flex items-center gap-2">
                    <GoDotFill
                      size={16}
                      className="text-color-500"
                    />
                    <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('registered information')}
                    </span>
                  </div>
                )}
              />
              <Pure>
                <FieldsFactory formSchema={[formSchema.last_enrollment_time]} />
              </Pure>
            </div>
          </div>
        </ActionModalWrapper>
      </React.Fragment>
    </Formik>
  )
}

export const EditCourseModal = ({
  owner_id,
  owner_type,
  onCancel = NullAsDefault,
  onSubmit = NullAsDefault,
  initialValues = {},
}) => {
  const t = useTranslate()
  const [current, setCurrent] = useState('overview')

  const _initialValues = useMemo(() => {
    const {
      about,
      time_open,
      time_close,
      last_enrollment_time,
      metadata = [],
      categories,
      ...rest
    } = initialValues ?? {}
    const status_options = [
      course_status_options.normal,
      course_status_options.closed,
    ].map(deepTranslate(t))
    return {
      ...rest,
      owner_id,
      owner_type,
      status_options,
      medal_id: rest?.medal?.id,
      about: convertFromString(about),
      time_open: parseDayjs(time_open),
      time_close: parseDayjs(time_close),
      last_enrollment_time: parseDayjs(last_enrollment_time),
      metadata: metadata ?? [],
      categories: _.isArray(categories)
        ? categories.map((category) => category?.id)
        : [],
    }
  }, [initialValues, owner_id, owner_type])

  return (
    <Formik
      validate={validate(t, "Edit")}
      validateOnMount={true}
      validateOnChange={true}
      enableReinitialize={true}
      initialValues={_initialValues}
      onSubmit={(values) => {
        const changes = shallowDiff(values, _initialValues)
        const {
          about,
          resetKey,
          owner_id,
          owner_type,
          time_open,
          time_close,
          status_options,
          last_enrollment_time,
          metadata,
          additional_fields,
          categories,
          ...rest
        } = changes ?? {}
        onSubmit(
          _.omitBy(
            {
              ...rest,
              metadata: JSON.stringify(metadata),
              time_open: getDateString(time_open),
              time_close: getDateString(time_close),
              about: about ? convertToString(about) : undefined,
              additional_fields: additional_fields
                ? JSON.stringify(additional_fields)
                : undefined,
              last_enrollment_time: getDateString(last_enrollment_time),
              categories: categories
                ? Array.from(categories).join(',')
                : undefined,
            },
            _.isUndefined
          )
        )
      }}>
      <React.Fragment>
        {/*<ErrorSummaries />*/}
        <ActionModalWrapper
          tab={current}
          action={<FormActionBar submitText="save" />}
          onTabChange={(key) => {
            setCurrent(key)
          }}
          tabs={[
            {
              key: 'overview',
              label: 'overview',
              component: ({ child }) => child,
            },
            {
              key: 'metadata',
              label: 'manage student information by course',
              component: () => (
                <FieldsFactory formSchema={[formSchema.metadata]} />
              ),
            },
            {
              key: 'about',
              label: 'about course',
              component: () => (
                <FieldsFactory formSchema={[formSchema.about]} />
              ),
            },
            {
              key: 'medal',
              label: 'certification',
              component: () => (
                <div className="flex flex-col gap-3">
                  <FieldsFactory formSchema={[formSchema.medal_id]} />
                  <div className="font-medium text-color-100 italic">
                    {`* ${t('explain certification')}`}
                  </div>
                  <div className="space-x-1 text-sm italic">
                    <span className="font-medium text-color-100">
                      {`* ${t('note')} : `}
                    </span>
                    <span className="text-color-400">
                      {t('note explain certification')}
                    </span>
                  </div>
                </div>
              ),
            },
          ]}>
          <Pure>
            <FieldsFactory
              formSchema={[
                formSchema.title,
                formSchema.idname,
                formSchema.description,
                formSchema.categories,
                formSchema.academic_field,
                formSchema.started_finished_date,
                formSchema.last_enrollment_time,
              ]}
            />
          </Pure>

          <Pure>
            <div className="flex flex-col gap-3">
              <ListHeaderLineAndDes
                className="pt-2 mt-3"
                renderTitle={() => (
                  <div className="flex items-center gap-2">
                    <BsShieldFillExclamation
                      size={16}
                      className="text-color-500"
                    />
                    <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {`${t('additional information')} (${t('manage')})`}
                    </span>
                  </div>
                )}
                description={t(
                  'additional information course manager description'
                )}
              />
              <div className="ml-2 flex flex-col gap-3">
                <FieldsFactory formSchema={[formSchema.additional_fields]} />
              </div>
            </div>
          </Pure>

          {/*<AdvancedFormFields formSchema={[formSchema.status]}/>*/}

          <Pure>
            <div className="flex flex-col gap-3">
              <HeaderWithIconLine
                className="pt-2 mt-3"
                renderTitle={() => (
                  <div className="flex items-center gap-2">
                    <BsShieldFillExclamation
                      size={16}
                      className="text-color-500"
                    />
                    <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('privacy')}
                    </span>
                  </div>
                )}
              />
              <div className="ml-2 flex flex-col gap-3">
                <FieldsFactory
                  formSchema={[
                    formSchema.status,
                    formSchema.privacy,
                    formSchema.channel_post_permission,
                  ]}
                />
              </div>
            </div>
          </Pure>
        </ActionModalWrapper>
      </React.Fragment>
    </Formik>
  )
}

export const CopyCourseModal = ({
  owner_id,
  owner_type,
  onCancel = NullAsDefault,
  onSubmit = NullAsDefault,
  initialValues = {},
}) => {
  const t = useTranslate()
  const [current, setCurrent] = useState('overview')

  const _initialValues = {
    title: _.get(initialValues, 'title'),
  }

  return (
      <Formik
          validate={validate(t)}
          validateOnMount={true}
          validateOnChange={true}
          enableReinitialize={true}
          initialValues={_initialValues}
          onSubmit={(values) => {
            onSubmit({
              ...values,
            })
          }}>
        <React.Fragment>
          {/*<ErrorSummaries />*/}
          <div className="flex flex-col gap-4">
            <FieldsFactory formSchema={[formSchema.title]} />
            <FieldsFactory formSchema={[formSchema.started_finished_date]} />
          </div>
          <FormActionBar submitText="duplicate" />
        </React.Fragment>
      </Formik>
  )
}

export const TutorialCourseModalContent = ({ onCancel, onHandleCreate }) => {
  const t = useTranslate()

  const ContentBlock = ({
    right = false,
    style,
    icon,
    title,
    content,
    renderContent,
    className,
  }) => (
    <div
      style={style}
      className={classNames(
        'cursor-pointer hover:shadow-items-primary border border-primary-200 hover:border-primary-600 relative',
        className
      )}>
      <div className="flex flex-col">
        <div
          style={{
            borderTopLeftRadius: right === true ? 5 : 20,
            borderTopRightRadius: 20,
          }}
          className="flex items-center gap-2 px-3 py-2 bg-primary-50 leading-tight">
          {icon}
          <span className="font-bold text-primary uppercase tracking-wider">
            {t(title)}
          </span>
        </div>
        {renderIfElse(
          renderContent,
          renderContent && renderContent(),
          <div className="px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
            {t(content)}
          </div>
        )}
        <div className="text-right mx-3 mb-1 text-2xs text-color-500 hover:text-color-100 italic">
          {`${t('more')}  >>`}
        </div>
      </div>
    </div>
  )

  const PopupBlock = ({ content, subTitle, example, className }) => (
    <div className={classNames('flex flex-col gap-1', className)}>
      <div className="text-xs text-color-200 italic">{t(content)}</div>
      <div className="text-sm text-color-500 italic leading-tight">
        {t(subTitle)}
      </div>
      <div className="ml-1">{example}</div>
    </div>
  )

  const ItemExample = ({ title }) => (
    <span className="space-x-1">
      <GoDotFill
        size={10}
        className="text-color-500"
      />
      <span className="text-xs italic">{title}</span>
    </span>
  )

  const CustomPopover = ({ popupContent, content }) => (
    <Popover
      classNames={{ root: 'PopupContent' }}
      placement="left"
      mouseEnterDelay={0.3}
      destroyOnHidden
      trigger="click"
      content={popupContent}>
      {content}
    </Popover>
  )

  return (
    <div className="flex flex-col gap-4">
      {/*<ListHeaderWithLine title="Course Management Guide" line={false} className="text-xl"/>*/}
      <div className="grid items-center grid-cols-2 gap-6">
        <CustomPopover
          popupContent={
            <PopupBlock
              className="p-1"
              content="course tutorial extra description"
              subTitle="example"
              example={
                <div className="flex flex-col gap-1">
                  <ItemExample title="Khóa Huấn luyện Huynh trưởng cấp I - 2024" />
                  {/*<ItemExample title="Khóa Huấn luyện Huynh trưởng cấp II - 2024" />*/}
                </div>
              }
            />
          }
          content={
            <div>
              <ContentBlock
                style={{ borderRadius: '20px 20px 5px 20px' }}
                icon={
                  <PiChalkboardSimpleBold
                    size={14}
                    className="text-primary"
                  />
                }
                title="course"
                content="course tutorial description"
                renderContent={() => (
                  <div className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                    <div className="text-color text-sm font-medium leading-tight">
                      {t(
                        'A Course is a feature that provides information about content, objectives, and training opportunities, managing basic teaching content.'
                      )}
                    </div>
                    <div>
                      {t(
                        'The goal of creating a Course is to publish the teaching program (content, timing) and outcomes after the training process for students.'
                      )}
                    </div>
                    <div>
                      {t(
                        "It allows learners to view information and directly register for the course through the 'Course Registration' function."
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          }
        />
        <div className="flex flex-center">
          <LuCornerRightDown
            size={60}
            style={{ color: '#e7e7e7' }}
          />
        </div>
      </div>
      <div className="grid items-center grid-cols-2 gap-6">
        <div className="flex flex-center">
          <LuCornerLeftDown
            size={60}
            style={{ color: '#e7e7e7' }}
          />
        </div>
        <CustomPopover
          popupContent={
            <PopupBlock
              content="classes tutorial extra description"
              subTitle="example"
              example={
                <div className="flex flex-col gap-1">
                  <ItemExample title="Lớp 1 - 1" />
                  <ItemExample title="Lớp giáo lý dự tòng 1 ( buổi tối 2 4 6 )" />
                </div>
              }
            />
          }
          content={
            <div>
              <ContentBlock
                right={true}
                style={{ borderRadius: '5px 20px 20px 20px' }}
                icon={
                  <ClassroomSvg
                    size={13}
                    className="text-primary"
                  />
                }
                title="classes"
                content="classes tutorial description"
                renderContent={() => (
                  <div className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                    <div className="text-color text-sm font-medium leading-tight">
                      {t(
                        'Classes under the Course plan effective teaching through organized classes.'
                      )}
                    </div>
                    <div>
                      {t(
                        'A class is an administrative unit created to ensure effective organization of teaching.'
                      )}
                    </div>
                    <div>
                      {t(
                        'Add classes in the management section for each course. Class division is based on the need for organizing teaching, usually depending on the number of students, and timing according to groups of students.'
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          }
        />
      </div>
      <div className="grid items-center grid-cols-2 gap-6">
        <CustomPopover
          popupContent={
            <div className="flex flex-col">
              <PopupBlock
                content="attendance tutorial extra description"
                subTitle="example"
                example={
                  <div className="flex flex-col">
                    <ItemExample title="Bảng điểm danh 1" />
                    <ItemExample title="Bảng điểm danh 2" />
                  </div>
                }
              />
              <div className="border-b my-1" />
              <PopupBlock
                content="grade tutorial extra description"
                subTitle="example"
                example={
                  <div className="flex flex-col">
                    <ItemExample title="Bảng điểm 1" />
                    <ItemExample title="Bảng điểm 2" />
                  </div>
                }
              />
            </div>
          }
          content={
            <div>
              <ContentBlock
                style={{ borderRadius: '20px 20px 5px 20px' }}
                icon={
                  <TbTimelineEventText
                    size={13}
                    className="text-primary"
                  />
                }
                title="activities during the training process"
                renderContent={() => (
                  <div className="flex flex-col gap-6 flex-1 px-3 py-2 my-4">
                    <div className="text-color text-sm font-medium leading-tight">
                      {t(
                        "Monitoring the training process: attendance and students' score results."
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-sm text-color-000 uppercase">
                        <GoDotFill
                          size={10}
                          className="text-color-000 mr-1"
                        />
                        {`${t('attendances')} : `}
                      </span>
                      <span className="ml-2 text-xs text-color-300 italic">
                        {t(
                          'Record participation through the attendance function in the class management section.'
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-sm text-color-000 uppercase">
                        <GoDotFill
                          size={10}
                          className="text-color-000 mr-1"
                        />
                        {`${t('grades')} : `}
                      </span>
                      <span className="ml-2 text-xs text-color-300 italic">
                        {t(
                          'Record training results through the results of exams organized during the course.'
                        )}
                      </span>
                    </div>
                  </div>
                )}
              />
            </div>
          }
        />
        <div className="flex flex-center">
          <LuCornerRightDown
            size={60}
            style={{ color: '#e7e7e7' }}
          />
        </div>
      </div>
      <div className="grid items-center grid-cols-2 gap-6">
        <div className="flex flex-center">
          <LuCornerLeftDown
            size={60}
            style={{ color: '#e7e7e7' }}
          />
        </div>
        <CustomPopover
          popupContent={
            <PopupBlock
              className="p-1"
              content="score summary tutorial extra description"
              subTitle="example"
              example={
                <div className="flex flex-col gap-1">
                  <ItemExample title="Lớp học -> Học viên -> Đánh giá cuối khóa" />
                  <ItemExample title="Workspace -> Khóa học -> QL thông tin dưới khóa -> Lớp học -> Học viên -> Đánh giá cuối khóa" />
                </div>
              }
            />
          }
          content={
            <div>
              <ContentBlock
                right={true}
                style={{ borderRadius: '5px 20px 20px 20px' }}
                icon={
                  <TbSum
                    size={13}
                    className="text-primary"
                  />
                }
                title="End-of-Course Evaluation"
                content="score summary tutorial description"
                renderContent={() => (
                  <div className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                    <div className="text-color text-sm font-medium leading-tight">
                      {t('Synthesize final results and assess attendance.')}
                    </div>
                    <div>
                      {t(
                        'Access the class management section, evaluate the end of the course for each student to view and record results (including attendance assessment and exam results, helping to comprehensively assess performance).'
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          }
        />
      </div>

      <div className="grid items-center grid-cols-2 gap-6">
        <div className="flex flex-col flex-center gap-2 py-10">
          <Button
            type="primary"
            className="rounded-lg no-border"
            onClick={() => onHandleCreate && onHandleCreate()}>
            {t('create course')}
          </Button>
          <span className="text-sm text-color-500 italic">
            {`* ${t('click here to create a new course')}`}
          </span>
        </div>
      </div>
    </div>
  )
}
